<script setup lang="ts">
import { useId } from 'vue'
import AdvancedSettings from './AdvancedSettings.vue'
import BinaryReferencePicker from './BinaryReferencePicker.vue'
import { referenceOf } from './binary-reference'
import { limitFromText } from './batch-limit'
import type { BatchServiceModel, BinaryReference } from '@/types'

const props = defineProps<{
  modelValue: BatchServiceModel
}>()

const emit = defineEmits<{
  'update:modelValue': [value: BatchServiceModel]
}>()

function update(changes: Partial<BatchServiceModel>) {
  emit('update:modelValue', { ...props.modelValue, ...changes })
}

// Source is a picker only. Publish refuses an expression there, because an inline array cannot be streamed.
function updateSource(reference: BinaryReference) {
  update({ source: { $type: 'binary', reference } })
}

function limitFrom(event: Event): number | null {
  return limitFromText((event.target as HTMLInputElement).value)
}

const maxRowsId = useId()
const maxBytesId = useId()
</script>

<template>
  <div>
    <div class="mb-3">
      <div class="form-label">Source</div>
      <BinaryReferencePicker
        label="Source"
        :model-value="referenceOf(modelValue.source)"
        @update:model-value="updateSource"
      />
    </div>

    <div class="row g-3">
      <div class="col-6 col-sm-4">
        <label class="form-label" :for="maxRowsId">Max rows</label>
        <input
          :id="maxRowsId"
          type="number"
          min="1"
          class="form-control"
          :value="modelValue.maxRows ?? ''"
          @input="update({ maxRows: limitFrom($event) })"
        />
      </div>
      <div class="col-6 col-sm-4">
        <label class="form-label" :for="maxBytesId">Max bytes</label>
        <input
          :id="maxBytesId"
          type="number"
          min="1"
          class="form-control"
          :value="modelValue.maxBytes ?? ''"
          @input="update({ maxBytes: limitFrom($event) })"
        />
      </div>
    </div>
    <div class="form-text mb-3">Set at least one limit.</div>

    <AdvancedSettings />
  </div>
</template>
